import { FeatureAnalysisRowLevel as Level } from "@models/platform-analysis/enums/feature/featureAnalysisRowLevel";
import { TableViewMode } from "@models/platform-analysis/enums/tableViewMode";
import { styled } from "@mui/material";
import { AnalysisVirtualizedTableWrapper } from "@pages/platform-analysis-v2/_id/components/virtualized-table/components/table-components/wrapper";
import { AnalysisSummaryStyleConfigs as Configs } from "../../../../configs";

interface SummaryAnalysisTableWrapperProps
  extends React.ComponentProps<typeof AnalysisVirtualizedTableWrapper> {
  isAppendix?: boolean;
  viewMode?: TableViewMode;
  showAnalysisScore?: boolean;
  noSelectionColumn?: boolean;
}

export const SummaryAnalysisTableWrapper = styled(
  AnalysisVirtualizedTableWrapper,
  {
    shouldForwardProp: (prop) =>
      ![
        "isAppendix",
        "viewMode",
        "showAnalysisScore",
        "noSelectionColumn",
      ].includes(prop.toString()),
  },
)<SummaryAnalysisTableWrapperProps>(
  ({
    theme: { palette },
    isAppendix = false,
    viewMode = TableViewMode.Default,
    showAnalysisScore = false,
    noSelectionColumn = false,
  }) => {
    const isDetailMode = viewMode === TableViewMode.Detail;
    const showScore = (showAnalysisScore && isDetailMode) || !isDetailMode;

    const titleColumnWidth = noSelectionColumn
      ? Configs.NoSelectionTitleColumnWidth
      : isDetailMode
      ? Configs.FeatureLevelTitleColumnWidth
      : Configs.DefaultTitleColumnWidth;
    const selectionColumnWidth = noSelectionColumn
      ? 0
      : Configs.TotalFeaturesSelectedColumnWidth;
    const productColumnWidth = noSelectionColumn
      ? isAppendix
        ? Configs.AppendixDefaultProductColumnWidth
        : Configs.DefaultProductColumnWidth
      : isDetailMode
      ? isAppendix
        ? Configs.AppendixFeatureLevelProductColumnWidth
        : Configs.FeatureLevelProductColumnWidth
      : isAppendix
      ? Configs.AppendixDefaultProductColumnWidth
      : Configs.DefaultProductColumnWidth;

    return {
      gap: 0,
      thead: {
        th: {
          border: `1px solid ${palette.secondary[100]}`,
          borderRight: "none",
          transition: "opacity 0.3s ease-in-out",
          "&.SummaryAnalysisTableHead-Title": {
            minWidth: titleColumnWidth,
            maxWidth: titleColumnWidth,
          },
          "&.SummaryAnalysisTableHead-TotalSelections": {
            minWidth: selectionColumnWidth,
            maxWidth: selectionColumnWidth,
            border: noSelectionColumn ? "none" : undefined,
            overflow: noSelectionColumn ? "hidden" : undefined,
          },
          "&.SummaryAnalysisTableHead-Product": {
            minWidth: productColumnWidth,
            maxWidth: productColumnWidth,
          },
          ":last-of-type": {
            borderRight: `1px solid ${palette.secondary[100]}`,
          },
        },
      },
      tbody: {
        borderBottom: "none",
        "& .SummaryAnalysisTableScoreCell.missing-essential": {
          "& .MuiTypography-root": {
            ":after": !isDetailMode && {
              content: '"*"',
              color: palette.error.main,
            },
          },
        },
        tr: {
          "&.SummaryAnalysisTableRow": {
            height: `${Configs.DefaultRowHeight}px`,
          },
          [`&.SummaryAnalysisTableRow-${Level.Feature}`]: {
            // visibility: isDetailMode ? "visible" : "collapse",
          },
          [`&.SummaryAnalysisTableRow-${Level.SubSection}`]: {
            // visibility: isDefaultMode || isDetailMode ? "visible" : "collapse",
            td: {
              backgroundColor: palette.secondary[50],
            },
          },
          [`&.SummaryAnalysisTableRow-${Level.Section}`]: {
            // visibility: "visible",
            td: {
              "& .MuiTypography-root": {
                fontSize: 14,
              },
            },
          },
          [`&.SummaryAnalysisTableRow-${Level.Feature},
            .SummaryAnalysisTableRow-${Level.SubSection}`]: {
            td: {
              backgroundColor: `${palette.secondary[50]} !important`,
              "& .MuiTypography-root": {
                fontWeight: 500,
                fontSize: 13,
              },
            },
          },
          "& .FeatureAnalysisRenderedCell": {
            borderTop: "none",
            borderRight: "none",
          },
          td: {
            borderLeft: `1px solid ${palette.secondary[100]}`,
            borderBottom: `1px solid ${palette.secondary[100]}`,
            ":last-of-type": {
              borderRight: `1px solid ${palette.secondary[100]}`,
            },
          },
          ":first-of-type": {
            td: {
              borderTop: `1px solid ${palette.secondary[100]}`,
              "& .MuiTypography-root": {
                fontWeight: 700,
                fontSize: 16,
              },
            },
          },
        },
        "& .SummaryAnalysisTableCell-Title": {
          width: titleColumnWidth,
        },
        "& .SummaryAnalysisTableCell-TotalSelections": {
          minWidth: selectionColumnWidth,
          maxWidth: selectionColumnWidth,
          border: noSelectionColumn ? "none" : undefined,
          padding: noSelectionColumn ? "0px !important" : undefined,
          overflow: noSelectionColumn ? "hidden" : undefined,
        },
        "& .SummaryAnalysisTableCell-TotalScore >.SummaryAnalysisTableScoreCell":
          {
            opacity: showScore ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
          },
        "& .SummaryAnalysisTableCell-TotalScore,.SummaryAnalysisTableCell-Data":
          {
            minWidth: productColumnWidth,
            maxWidth: productColumnWidth,
          },
        [`& .SummaryAnalysisTableCell-${Level.Section}`]: {
          backgroundColor: palette.common.white,
        },
        [`& .SummaryAnalysisTableCell-${Level.SubSection}
          ,.SummaryAnalysisTableCell-${Level.Feature}`]: {
          backgroundColor: `${palette.secondary[50]} !important`,
        },
      },
      tfoot: {
        "& .SummaryAnalysisTableFooter-MissingEssentialNote": {
          transition: "opacity 0.3s ease-in-out",
          width: "fit-content",
          display: isDetailMode ? "none" : undefined,
        },
        "& .SummaryAnalysisTableFooter-ContainsEssential": {
          transition: "opacity 0.3s ease-in-out",
          width: "fit-content",
          display: !isDetailMode ? "none" : undefined,
        },
      },
    };
  },
);
